import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ELYON_VISIBLE } from "@/lib/site";

// Desktop dropdown "Accompagnement" — ordre UX : informatif d'abord (comprendre, confiance), action ensuite (demander, créer)
// ── RÈGLE DES POINTS D'UNIVERS (appliquée partout : desktop + mobile ; le footer reste neutre, sans point) ──
// Le BLEU est la couleur de base de la marque : les entrées de l'univers immobilier ne portent PAS de point.
// Seuls les univers différenciants portent un point : ROSE = mode de vie / installation (Où vivre ?, Demander un accompagnement),
// VERT = entreprendre (Créer votre entreprise, Packs & tarifs).
// NB : l'ancien badge « étoile / Nouveau » a été retiré volontairement — les points d'univers sont l'unique système de signalement du menu (pas de doublon).
const NETWORK_GROUP = [
  { to: "/private-network", key: "privatenetwork" },
  { to: "/conseil-projets-immobiliers", key: "conseil" },
  { to: "/accompagnement", key: "accompagnement", u: "rose" },
  { to: "/entreprendre", key: "entreprendre", u: "green" },
  { to: "/entreprendre#packs", key: "packs", u: "green" },
];
// Groupe « Projets & Biens » : réalisations propres + biens à la vente
const PROPERTY_GROUP = [
  { to: "/projets", key: "projets" },
  { to: "/opportunites", key: "opportunites" },
];

// R4 — menu allégé à 6 entrées (À propos vit désormais dans le footer uniquement).
// Elyon (concept) réapparaît automatiquement en tête de menu si ELYON_VISIBLE repasse à true.
const DESKTOP_LINKS = [
  ...(ELYON_VISIBLE ? [{ to: "/concept", key: "concept" }] : []),
  { to: "/approche", key: "approche" },
  { key: "biens", group: PROPERTY_GROUP },
  { to: "/lifematch", key: "ouvivre", u: "rose" },
  { key: "reseau", group: NETWORK_GROUP },
  { to: "/novatv", key: "novatv" },
  { to: "/investisseurs", key: "investisseurs" },
];

// Couleurs des points d'univers — variantes fond clair / fond sombre (menu mobile)
const UNIVERSE_DOT = { blue: "#2E8BFF", rose: "#B3495E", green: "#2F9E44" };
const UNIVERSE_DOT_DARK = { blue: "#4DB8FF", rose: "#F2A69B", green: "#79D83D" };
const UniverseDot = ({ u, dark }) =>
  u ? (
    <span
      aria-hidden="true"
      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
      style={{ backgroundColor: (dark ? UNIVERSE_DOT_DARK : UNIVERSE_DOT)[u] }}
    />
  ) : null;

const DARK_HERO_ROUTES = ["/", "/concept", "/investisseurs", "/novatv", "/lifematch", "/private-network"];

const LangToggle = ({ light }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  return (
    <div
      className={`flex items-center rounded-full border p-0.5 text-[11px] font-semibold tracking-wide ${
        light ? "border-white/30" : "border-[#111827]/20"
      }`}
      data-testid="lang-toggle"
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => i18n.changeLanguage(l)}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors duration-300 ${
            lang === l
              ? "bg-[#2E8BFF] text-white"
              : light
              ? "text-white/70 hover:text-white"
              : "text-[#111827]/60 hover:text-[#111827]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

// Premium full-screen mobile menu — motion choreography
// Ouverture rapide (0.25s) + items en parallèle du fondu (pas de "beforeChildren") : évite l'écran sombre vide
const menuVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25, ease: "easeOut", delayChildren: 0.08, staggerChildren: 0.04 } },
  exit: { opacity: 0, transition: { duration: 0.22, ease: "easeInOut" } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 320, damping: 30 } },
  exit: { opacity: 0, y: 6, transition: { duration: 0.12 } },
};

const BurgerIcon = ({ open }) => (
  <span className="relative block h-[14px] w-5" aria-hidden="true">
    <motion.span
      animate={open ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className="absolute left-0 top-0 block h-[1.5px] w-full rounded-full bg-current"
    />
    <motion.span
      animate={open ? { opacity: 0, x: 10 } : { opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
      className="absolute left-0 top-[6px] block h-[1.5px] w-full rounded-full bg-current"
    />
    <motion.span
      animate={open ? { rotate: -45, y: -6.5 } : { rotate: 0, y: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className="absolute bottom-0 left-0 block h-[1.5px] w-full rounded-full bg-current"
    />
  </span>
);

export const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Ferme le menu mobile sur TOUTE navigation (y compris hash-only, ex. /entreprendre → /entreprendre#packs),
  // sinon body{overflow:hidden} du scroll-lock bloque le scroll vers l'ancre
  useEffect(() => setOpen(false), [location]);

  // Scroll-lock while the mobile menu is open (le reset des sous-menus se fait après l'anim de sortie — voir onExitComplete)
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isDarkHero = DARK_HERO_ROUTES.includes(pathname) || /^\/projets\/.+/.test(pathname);
  const light = open || (isDarkHero && !scrolled);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        open
          ? "bg-[#0B1422]" // fond sombre porté par le header lui-même dès l'ouverture → jamais de texte blanc sur fond blanc
          : scrolled
          ? "border-b border-[#111827]/5 bg-white/75 shadow-[0_1px_24px_rgba(17,24,39,0.06)] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[74px] max-w-[1440px] items-center gap-4 px-5 lg:px-8">
        <div className="shrink-0">
          <Logo light={light} />
        </div>

        <nav className="hidden flex-1 items-center justify-center gap-x-1 xl:flex" data-testid="desktop-nav">
          {DESKTOP_LINKS.map((item) => {
            if (item.group) {
              const groupActive = item.group.some((g) => pathname.startsWith(g.to));
              return (
                <div key={item.key} className="group relative" data-testid={`nav-${item.key}-group`}>
                  <button
                    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-lg px-2 py-2 text-[13px] font-medium transition-colors duration-300 ${
                      light
                        ? groupActive
                          ? "text-white"
                          : "text-white/65 group-hover:bg-white/10 group-hover:text-white"
                        : groupActive
                        ? "text-[#2E8BFF]"
                        : "text-[#111827]/55 group-hover:bg-[#111827]/[0.04] group-hover:text-[#111827]"
                    }`}
                  >
                    {t(`nav.${item.key}`)}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                  </button>
                  <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div className="w-[330px] rounded-2xl border border-[#111827]/8 bg-white p-2 shadow-xl shadow-[#111827]/10">
                      {item.group.map(({ to, key, u }) => (
                        <NavLink
                          key={key}
                          to={to}
                          data-testid={`nav-${key}-link`}
                          className={({ isActive }) =>
                            `block rounded-xl px-4 py-3 transition-colors duration-200 ${
                              isActive ? "bg-[#2E8BFF]/8" : "hover:bg-[#111827]/[0.04]"
                            }`
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <span className={`flex items-center gap-2 text-[13px] font-medium ${isActive ? "text-[#2E8BFF]" : "text-[#111827]/80"}`}>
                                <UniverseDot u={u} />
                                {t(`nav.${key}`)}
                              </span>
                              <span className="mt-0.5 block text-[11.5px] font-normal leading-snug text-[#111827]/45">
                                {t(`nav.desc.${key}`)}
                              </span>
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            const { to, key, u } = item;
            return (
              <NavLink
                key={key}
                to={to}
                data-testid={`nav-${key}-link`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-2 text-[13px] font-medium transition-colors duration-300 ${
                    light
                      ? isActive
                        ? "text-white"
                        : "text-white/65 hover:bg-white/10 hover:text-white"
                      : isActive
                      ? "text-[#2E8BFF]"
                      : "text-[#111827]/55 hover:bg-[#111827]/[0.04] hover:text-[#111827]"
                  }`
                }
              >
                {t(`nav.${key}`)}
                <UniverseDot u={u} dark={light} />
              </NavLink>
            );
          })}
        </nav>

        <div className="flex shrink-0 items-center gap-4">
          <LangToggle light={light} />
          <span
            aria-hidden="true"
            className={`hidden h-5 w-px xl:block ${light ? "bg-white/25" : "bg-[#111827]/12"}`}
          />
          <Link
            to="/j-ai-un-terrain"
            data-testid="header-cta-btn"
            className="hidden items-center gap-2 rounded-xl shadow-sm bg-[#2E8BFF] px-5 py-2.5 text-[13px] font-semibold text-white transition-transform duration-300 hover:scale-105 hover:brightness-110 sm:inline-flex"
          >
            {t("nav.cta")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={open}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-300 xl:hidden ${
              light ? "text-white hover:bg-white/10" : "text-[#111827] hover:bg-[#111827]/5"
            }`}
          >
            <BurgerIcon open={open} />
          </button>
        </div>
      </div>

      <AnimatePresence onExitComplete={() => setSubOpen({})}>
        {open && (
          <motion.div
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-[-1] flex flex-col bg-[#0B1422] xl:hidden"
            data-testid="mobile-menu"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(120% 55% at 88% -8%, rgba(46,139,255,0.22), transparent 62%), radial-gradient(90% 45% at -5% 108%, rgba(77,184,255,0.10), transparent 60%)",
              }}
            />
            <nav className="relative flex-1 overflow-y-auto px-7 pb-4 pt-[100px]" aria-label="Menu mobile">
              {DESKTOP_LINKS.map((item) => {
                if (item.group) {
                  const groupActive = item.group.some((g) => pathname.startsWith(g.to));
                  return (
                    <motion.div key={item.key} variants={itemVariants}>
                      <button
                        data-testid={`mobile-nav-${item.key}-toggle`}
                        onClick={() => setSubOpen((s) => ({ ...s, [item.key]: !s[item.key] }))}
                        aria-expanded={!!subOpen[item.key]}
                        className="flex w-full items-center justify-between py-[13px] text-left"
                      >
                        <span
                          className={`inline-flex items-center gap-2 font-display text-[18px] font-medium leading-none tracking-[0.01em] transition-colors duration-300 ${
                            groupActive ? "text-[#F2A69B]" : "text-white/90"
                          }`}
                        >
                          {t(`nav.${item.key}`)}
                        </span>
                        <motion.span
                          animate={{ rotate: subOpen[item.key] ? 180 : 0 }}
                          transition={{ type: "spring", stiffness: 350, damping: 26 }}
                          className="text-white/40"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </motion.span>
                      </button>
                      <AnimatePresence initial={false}>
                        {subOpen[item.key] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="flex flex-col border-l border-white/10 pb-2 pl-4">
                              {item.group.map(({ to, key, u }) => (
                                <NavLink
                                  key={key}
                                  to={to}
                                  data-testid={`mobile-nav-${key}-link`}
                                  className="block py-[9px]"
                                >
                                  {({ isActive }) => (
                                    <>
                                      <span
                                        className={`flex items-center gap-2 font-display text-[15px] font-normal leading-none tracking-[0.01em] transition-colors duration-300 ${
                                          isActive ? "text-[#F2A69B]" : "text-[#C9D5E6]"
                                        }`}
                                      >
                                        <UniverseDot u={u} dark />
                                        {t(`nav.${key}`)}
                                      </span>
                                      <span className="mt-1 block text-[11.5px] font-normal leading-snug text-white/35">
                                        {t(`nav.desc.${key}`)}
                                      </span>
                                    </>
                                  )}
                                </NavLink>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                }
                const { to, key, u } = item;
                return (
                  <motion.div key={key} variants={itemVariants}>
                    <NavLink
                      to={to}
                      data-testid={`mobile-nav-${key}-link`}
                      className="group flex items-center gap-2.5 py-[13px]"
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-full bg-[#4DB8FF]" />
                          )}
                          <span
                            className={`inline-flex items-center gap-2 font-display text-[18px] font-medium leading-none tracking-[0.01em] transition-colors duration-300 ${
                              isActive ? "text-[#7CC4FF]" : "text-white/90 group-hover:text-white"
                            }`}
                          >
                            {t(`nav.${key}`)}
                            <UniverseDot u={u} dark />
                          </span>
                        </>
                      )}
                    </NavLink>
                  </motion.div>
                );
              })}
            </nav>
            <motion.div
              variants={itemVariants}
              className="relative border-t border-white/10 px-7 pt-5"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 26px)" }}
            >
              <Link
                to="/j-ai-un-terrain"
                data-testid="mobile-cta-btn"
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#2E8BFF] px-6 py-3.5 font-display text-[14px] font-semibold tracking-[0.01em] text-white shadow-[0_8px_28px_rgba(46,139,255,0.3)] transition-transform duration-300 active:scale-[0.98]"
              >
                {t("nav.cta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-4 text-center text-[11.5px] tracking-[0.02em] text-white/35">
                +230 5 518 3296 · support@novaimmo.mu
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
